/**
 * Demo Simulation — the wallet-shell wrapped hub. Internal-only surface for
 * building out end-to-end simulations over time. Distinct from the public
 * pitch page at `/demos` (which uses its own dark-navy chrome, not the wallet
 * shell).
 *
 * As new demos land, add a card to DEMOS below.
 */
import { Link } from "react-router-dom";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  ShieldCheck, ArrowRight, Sparkles, Clock, Users, Zap, FlaskConical, Building2, KeyRound, MessageCircle,
} from "lucide-react";

const DEMOS = [
  {
    id: "altara-prologue",
    to: "/wallet/demos/altara-prologue",
    external: false,
    eyebrow: "Prologue · storyline opener",
    title: "Why Altara exists — 30-second opener",
    body: "Five cinematic beats that lead into Nomsa's journey. Altara title → we can't control everything a consumer sees → who carries the burden → what if we asked a different question → the trust & intelligence layer.",
    icon: ShieldCheck,
    duration: "≈ 30 sec",
    audience: "Judges · Everyone",
    highlights: [
      "5 cinematic beats + Ken-Burns motion",
      "Uses the actual pitch-deck artwork",
      "Sits in front of the 6-slide Nomsa journey",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    id: "abc-wealth-proposal",
    to: "/wallet/demos/abc-wealth-proposal",
    external: false,
    eyebrow: "Institution notification",
    title: "ABC Wealth sends a proposal to Nomsa",
    body: "Split-screen showcase. James Smith drafts an investment proposal in the ABC Wealth adviser console. One tap delivers it into Nomsa's Altara wallet as a governed Trust Check notification — animated envelope, phone chime, and the actual 4-stage journey opens live.",
    icon: Building2,
    duration: "≈ 90 sec",
    audience: "Institutions · Product · Sales",
    highlights: [
      "Real POST /admin/decisions/demo-send call — not a mock",
      "Live TRUST_PENDING decision lands on Nomsa's device",
      "Animated phone notification + on-screen toast + inbox nudge",
      "Deep-links straight into the governed decision journey",
    ],
    status: "live",
  },
  {
    id: "altara-trust-verify",
    to: "/wallet/demos/altara-trust-verify",
    external: false,
    eyebrow: "Trust stage · slide asset",
    title: "Altara verifies the adviser and institution",
    body: "Three verification checks flip green one after the other, evidence rows slide in with real values (FAIS reg no · FSP #48291 · KY-I registry), then the Trust chip lights up in gold. Purpose-built for embedding in a PowerPoint deck alongside the ABC Wealth proposal animation.",
    icon: ShieldCheck,
    duration: "≈ 6 sec",
    audience: "Institutions · Compliance · Sales",
    highlights: [
      "Deterministic Nomsa scenario (James Smith · FAIS #A-2214-JS)",
      "Compares against My Trusted Institutions + Altara KY-I",
      "Trust chip glows gold once all three checks pass",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    id: "altara-understand",
    to: "/wallet/demos/altara-understand",
    external: false,
    eyebrow: "Understand stage · slide asset",
    title: "Contextual education tracked as learning signals",
    body: "Every in-context question Nomsa asks NAVI about the proposal is hashed, timestamped, and streamed to a live wallet-signal feed. The signals are then packaged into a personalised learning intervention pack — micro-lesson, follow-up prompt, and an adviser signal — and her learning profile is filed in the Trust Wallet.",
    icon: MessageCircle,
    duration: "≈ 14 sec",
    audience: "Judges · Institutions · Consumer",
    highlights: [
      "3 in-context questions on the actual proposal",
      "Live signal feed — every question tracked + hashed",
      "Auto-packaged into a learning intervention pack",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    id: "altara-check-act",
    to: "/wallet/demos/altara-check-act",
    external: false,
    eyebrow: "Check + Act stages · slide asset",
    title: "Check the fit · Record the intent",
    body: "Altara compares Nomsa's monthly commitment against her disposable income (17% — well below the 25% pressure threshold), then Nomsa selects 'Ready to proceed'. A hash-chained Trust Certificate is sealed and delivered to ABC Wealth. Altara never moves money.",
    icon: ShieldCheck,
    duration: "≈ 13 sec",
    audience: "Judges · Institutions · Compliance",
    highlights: [
      "Live 'fits comfortably' banner with 17% progress bar",
      "Two intent options — More info or Ready to proceed",
      "Certificate seal with real Ed25519 signature + hash chain",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    id: "altara-complaint",
    to: "/wallet/demos/altara-complaint",
    external: false,
    eyebrow: "Post-sale · slide asset",
    title: "Months later — Nomsa raises a complaint",
    body: "Nomsa types what happened in plain language. NAVI probes with three quick clarifying questions to make the case complete. Altara packages the whole thing into an Ombudsman-ready bundle, hash-chains it into her Trust Wallet, and delivers it to ABC Wealth in one tap.",
    icon: ShieldCheck,
    duration: "≈ 11 sec",
    audience: "Judges · Compliance · Ombud",
    highlights: [
      "Typing animation of Nomsa's actual complaint",
      "NAVI probing — three quick clarifiers",
      "Package sealed + delivered + Ombud copy filed",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    id: "altara-trust-check-scam",
    to: "/wallet/demos/altara-trust-check-scam",
    external: false,
    eyebrow: "Trust & Safety · slide asset",
    title: "Trust Check on a DirectAxis loan email",
    body: "Nomsa uploads a suspicious email + PDF from an institution Altara doesn't participate with. Altara analyses the sender domain, offer plausibility, attachment provenance, communication channels and greeting patterns — and returns a HIGH RISK verdict with five named findings, plus the official channels she should use if she wants to verify directly.",
    icon: ShieldCheck,
    duration: "≈ 15 sec",
    audience: "Judges · Consumer · Compliance",
    highlights: [
      "Works even for institutions Altara doesn't participate with",
      "Five named findings — no black-box scam score",
      "Recommendation + link to Trusted Institutions",
      "MP4 + GIF downloads for one-tap slide embedding",
    ],
    status: "live",
  },
  {
    to: "/demo/trust-certificate",
    external: false,
    eyebrow: "Cryptographic proof",
    title: "Trust Certificate — end-to-end walkthrough",
    body: "Advisor sends a real 20-year Term Life quote through the Altara pipeline. A cryptographically-signed Trust Certificate is minted. Drop the file in the verify widget → MATCH. Drop a random file → MISMATCH.",
    icon: ShieldCheck,
    duration: "≈ 3 min",
    audience: "Product · Compliance · Sales",
    highlights: [
      "One-click live scenario seed",
      "Real Ed25519 signature + published JWKS",
      "7 pre-ordered walkthrough deep-links",
      "Downloadable 95%-realistic demo quote PDF",
    ],
    status: "live",
  },
  {
    id: "branch-trust-package",
    to: "/wallet/demos/branch-trust-package",
    external: false,
    eyebrow: "Branch scenario",
    title: "Trust Package — issued at the branch",
    body: "Mrs Dlamini visits ABC Bank for funeral cover. Adviser generates the quote in the bank's own system. ONE API call turns that quote into a guided Consumer Trust Journey — printed badge, QR, secure link, and structured Consumer Intelligence flowing back to the CRM.",
    icon: Building2,
    duration: "≈ 4 min",
    audience: "Bancassurance · CX · Compliance",
    highlights: [
      "POST /api/trust-package — one call, full package",
      "Printable SVG Trust Badge with embedded QR",
      "Four hand-off channels (QR · SMS · WhatsApp · branch tablet)",
      "Consumer Intelligence Summary — signals, never answers",
    ],
    status: "live",
  },
  {
    id: "institution-api-key",
    to: "/wallet/demos/institution-api-key",
    external: false,
    eyebrow: "Integrator scenario · mock",
    title: "Institution API Key & Rate Limits",
    body: "Preview of production Trust Package auth. Institution self-serves an API key, calls /api/trust-package with Authorization: Bearer …, and gets a per-institution console showing rate-limit + issued-package registry.",
    icon: KeyRound,
    duration: "≈ 2 min",
    audience: "Integrators · Security · Product",
    highlights: [
      "One-time reveal + hash-at-rest key model",
      "Rate limit + tenant attribution on every call",
      "Per-institution package registry",
      "Mock only — live endpoint remains unauthenticated in MVP",
    ],
    status: "live",
  },
];

const PLANNED = [
  { title: "NAVI Scam Shield", body: "NAVI's grounded scam verdict, backed by the Altara scam corpus with 👍/👎 feedback closing the loop." },
  { title: "Consumer Assistance", body: "Consumer articulates a complaint → attaches evidence → package transfers to institution CRM with a reference number." },
];


export default function WalletDemosPage() {
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-8" data-testid="wallet-demos-page">
        <header className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD, color: NAVY }}>
            <FlaskConical className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Internal · demo simulation</div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>Demo Simulation</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">A dedicated space to run and iterate on end-to-end scenarios. Every card below is real running infrastructure. We&apos;ll add more simulations as they land.</p>
          </div>
        </header>

        <section>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Live simulations</div>
          <h2 className="font-display text-xl lg:text-2xl font-semibold mt-1" style={{ color: NAVY }}>
            {DEMOS.length} available now
          </h2>
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {DEMOS.map(d => <LiveCard key={d.id} demo={d} />)}
          </div>
        </section>
      </div>
    </WalletShell>
  );
}


function LiveCard({ demo }) {
  const Icon = demo.icon;
  return (
    <Link
      to={demo.to}
      data-testid={`wallet-demo-card-${demo.id}`}
      className="group rounded-2xl bg-white border shadow-sm p-5 lg:p-6 transition hover:shadow-lg hover:-translate-y-0.5 block"
      style={{ borderColor: `${GOLD}55` }}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
          <Icon className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{demo.eyebrow}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 mt-0.5 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
          </div>
        </div>
      </div>

      <h3 className="mt-4 font-display text-lg lg:text-xl font-semibold leading-snug" style={{ color: NAVY }}>{demo.title}</h3>
      <p className="mt-2 text-[12px] leading-relaxed text-slate-600">{demo.body}</p>

      <ul className="mt-4 space-y-1">
        {demo.highlights.map(h => (
          <li key={h} className="flex items-start gap-2 text-[11px] text-slate-700">
            <Sparkles className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: GOLD }} />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {demo.duration}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {demo.audience}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 font-semibold text-[12px] transition group-hover:gap-2.5" style={{ color: NAVY }}>
          Open <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}


function PlannedCard({ title, body }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 p-4 lg:p-5" data-testid="wallet-demo-planned">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100">
        <Zap className="w-4 h-4 text-slate-400" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3">Planned</div>
      <h3 className="mt-1 font-display text-base lg:text-lg font-semibold" style={{ color: NAVY }}>{title}</h3>
      <p className="mt-2 text-[12px] leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}
